import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatDate, parseTypedDate } from "../utils/dateFormat";
import CalendarPopover from "./CalendarPopover";
import { num } from "../utils/invoiceLineMath";

/**
 * Cells wrap. They used to be `whitespace-nowrap overflow-hidden text-ellipsis`,
 * which kept every column narrow at the cost of making long values unreadable:
 * a product description ran off to one side and the only way to see it was the
 * hover tooltip. Wrapping trades a taller row for a legible one.
 *
 * `break-words` so a long unbroken token — a product code, a URL — wraps instead
 * of forcing the column wider and pushing the grid sideways. The title
 * attribute stays, so hover still shows the value in full.
 */
const WRAP_CLASS = "whitespace-pre-wrap break-words";

type EditableCellProps = {
  value: any;
  type?: "text" | "number" | "date";
  onSave: (value: any) => Promise<void> | void;
  /**
   * Rejects a value before it is saved. Return an error message to block the
   * save, or null to allow it.
   *
   * Without this the cell accepted anything: a number column ran the draft
   * through Number() and saved `null` when it was not numeric, so a typo
   * silently ERASED the value it was meant to correct.
   */
  validate?: (value: any, raw: string) => string | null;
  /** True view-only mode: never enters edit mode, renders plain text. */
  readOnly?: boolean;
  /**
   * Hides the inline Save/Cancel buttons. Commit still happens on Enter or on
   * blur, cancel still happens on Escape — for callers with their own single
   * top-level Save button instead of one per cell.
   */
  hideActions?: boolean;
  /**
   * A unit written after the value when the cell is being READ — "%" on a VAT
   * rate, so 12.5 reads as 12.5%. Display only, deliberately: it never reaches
   * the input, so nobody has to edit around it, and it never reaches onSave, so
   * what is stored stays the bare number every consumer downstream expects.
   *
   * The point is that a rate column and a money column stop looking alike. A
   * rate typed into the amount column beside it is a silent, expensive mistake,
   * and the two were previously distinguishable only by their headers.
   */
  suffix?: string;
  /**
   * The value actually on the record, when `value` may show something else —
   * a fallback, or an unsaved edit. The no-op check has to agree with this AND
   * with what is shown before it treats a commit as "nothing changed".
   *
   * The Expiry cell shows the date read off the invoice until somebody types
   * one. Typing that same date is a person confirming it off the packaging and
   * must write it; compared against the shown fallback it was discarded as
   * "unchanged", `expiryDate` stayed empty, and Confirm & sync kept refusing a
   * line that looked dated (found while verifying USC-BUG-0057).
   */
  stored?: any;
  /**
   * What the cell SHOWS while not being edited, when that differs from
   * `value` itself — VIEW ONLY (D83, prod vendor-invoice 3723225809, Blue
   * Waters, USC-BUG-0068/0070/0076). The Discount cell reads a raw per-unit
   * figure (6.50) but shows the line's total discount (156.00); before this
   * prop existed, the only way to show a derived figure was to pass IT as
   * `value` itself, which also seeded the edit input and the "did this
   * change" check with it — so opening the cell and blurring without typing
   * anything compared the derived 156.00 against the stored 6.50, read that
   * as a change, and saved 156.00 over the real figure.
   *
   * Never reaches the input, never reaches `onSave`, and never enters the
   * "unchanged" comparison: `value` (and `stored`, when given) alone decide
   * what editing starts from and what counts as no-op. undefined falls back
   * to formatting `value` itself, exactly as every caller worked before this
   * prop existed — so a caller that only ever showed `value` is unaffected.
   */
  display?: any;
  /**
   * Extra tooltip text shown ahead of the usual one — "6.50 as printed" on the
   * Discount cell, "Read off the invoice — confirm against the packaging" on
   * Expiry. Callers used to put this on the surrounding `<td>` instead, which
   * never showed: this button/div fills the whole cell, so the ELEMENT under
   * the cursor is always this one, and its own title wins the tooltip.
   */
  hint?: string;
};

export default function EditableCell({ value, type = "text", onSave, validate, readOnly, hideActions, suffix, stored, display: displayOverride, hint }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Set by Escape just before it blurs the input, so the blur handler that
  // commits on blur (see hideActions) does not immediately re-save what
  // Escape just discarded.
  const cancelledRef = useRef(false);
  // A date cell opens a month calendar under its input while editing; a picked
  // day is committed through the same save() as a typed one.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // The stored value, verbatim. This is what the INPUT is seeded with — a date
  // input accepts yyyy-mm-dd and nothing else, so it must never be handed the
  // formatted text below.
  const raw = useMemo(() => {
    if (value === undefined || value === null || value === "") return "";
    return String(value);
  }, [value]);

  // What the cell READS as, which is a different question. A date is shown
  // dd/mm/yyyy like everywhere else in the system; the raw yyyy-mm-dd was
  // leaking onto the screen because storage and display were the same string.
  //
  // `displayOverride` (view-only, see its own prop comment) wins outright when
  // given, but through the SAME pipeline `raw` goes through below — String,
  // then date formatting, then suffix — so an Expiry override still reads
  // dd/mm/yyyy and a Discount override of 0 is a real value, not "empty"
  // (`0 ? x : y` is falsy; only null/undefined/"" mean nothing to show).
  const display = useMemo(() => {
    const source = displayOverride !== undefined ? displayOverride : raw;
    if (source === null || source === undefined || source === "") return null;
    const text = String(source);
    const shown = type === "date" ? formatDate(text) || text : text;
    // Not appended to an empty cell: "%" on its own reads as a value.
    return suffix ? `${shown}${suffix}` : shown;
  }, [raw, type, suffix, displayOverride]);

  // What the input starts with. A date is typed, and shown back, as dd/mm/yyyy.
  function seedText(r: string): string {
    return type === "date" ? formatDate(r) || r : r;
  }

  useEffect(() => {
    if (!editing) setDraft(seedText(raw));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, value]);

  function startEdit() {
    if (readOnly) return;
    setErr(null);
    setDraft(seedText(raw));
    setEditing(true);
  }

  function cancel() {
    cancelledRef.current = true;
    setErr(null);
    setDraft(seedText(raw));
    setEditing(false);
  }

  // `picked` is the text a calendar pick just put in the box: state has not
  // caught up with it yet, so it is passed in rather than read from `draft`.
  async function save(picked?: string) {
    const text = picked ?? draft;
    setErr(null);
    setSaving(true);
    try {
      let v: any = text;

      if (type === "number") {
        const trimmed = String(text).trim();
        if (trimmed === "") {
          v = null;
        } else {
          // Accept what people actually type into a money column — "1,250.00",
          // "$1250", "12.5%" — and reject what is not a number at all rather
          // than turning it into a blank. Read by the shared `num`, which knows
          // a decimal comma ("7,95" is 7.95, "1,250.00" is 1250): deleting every
          // comma here saved 795 (D117.2).
          const n = num(trimmed);
          if (n === null) {
            setErr(`"${trimmed}" is not a number`);
            setSaving(false);
            return;
          }
          v = n;
        }
      } else if (type === "date") {
        const trimmed = String(text).trim();
        if (!trimmed) {
          v = null;
        } else if (trimmed === seedText(raw)) {
          // Untouched: keep the stored text exactly as it was (it may not be ISO).
          v = raw;
        } else {
          // Typed text is read day-first and refused when it is not a real day
          // (D109.6); what is committed is still the ISO yyyy-mm-dd it always was.
          const iso = parseTypedDate(trimmed);
          if (!iso) {
            setErr(`"${trimmed}" is not a valid date — type dd/mm/yyyy`);
            setSaving(false);
            return;
          }
          v = iso;
        }
      } else {
        v = String(text);
        if (v.trim() === "") v = null;
      }

      const problem = validate?.(v, String(text));
      if (problem) {
        setErr(problem);
        setSaving(false);
        return;
      }

      // A no-op edit (clicked in, clicked away, nothing changed) is a cancel,
      // not a save — otherwise every cell touched-but-unchanged would get
      // marked dirty/manually-edited for no reason.
      //
      // "Nothing changed" has to hold against BOTH readings, which is why this
      // is an AND and not a choice between them (USC-BUG-0045):
      //  - against what the cell SHOWS, because typing the record's own value
      //    back over an unsaved edit is a change on screen; comparing only
      //    against the record would discard it and leave the withdrawn edit
      //    staged.
      //  - against what is ON THE RECORD, because the shown value may be a
      //    fallback (the expiry read off the invoice) or a figure a sibling
      //    cell's edit has just derived into an unsaved preview (Total Units,
      //    the moment Units / Case is typed). Typing either of those is a
      //    person stating it, and comparing only against the screen threw the
      //    keystroke away with nothing said.
      // Without `stored` the caller states no record value, and what the cell
      // shows is the only reading there is.
      //
      // A number cell compares as numbers: a stored "7,95" re-saved untouched
      // is 7.95 against "7,95" as text, and would be written back as a change.
      const sameValue = (was: any) =>
        String(v ?? "") === String(was ?? "") || (type === "number" && v !== null && num(was) === v);
      const sameAsShown = sameValue(value);
      const sameAsStored = stored === undefined || sameValue(stored);
      if (sameAsShown && sameAsStored) {
        setEditing(false);
        setSaving(false);
        return;
      }

      await onSave(v);
      setEditing(false);
    } catch (e: any) {
      setErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      setCalendarOpen(false);
      save();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setCalendarOpen(false);
      cancel();
    }
  }

  function onBlur() {
    setCalendarOpen(false);
    if (!hideActions) return;
    // A save already under way — typically waiting on an in-app confirmation,
    // whose dialog took focus and caused this blur — must not ask again (VI-6).
    if (saving) return;
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    save();
  }

  if (readOnly) {
    return (
      <div
        className={`w-full px-2 py-1 min-w-0 ${WRAP_CLASS}`}
        title={[hint, display].filter(Boolean).join("\n\n") || undefined}
      >
        {display ? (
          <span className="text-slate-900">{display}</span>
        ) : (
          <span className="text-slate-400 italic">empty</span>
        )}
      </div>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={startEdit}
        className={`w-full text-left rounded px-2 py-1 min-w-0 hover:bg-slate-50 transition ${WRAP_CLASS}`}
        title={[hint, display ? `${display}\n(click to edit)` : "Click to edit"].filter(Boolean).join("\n\n")}
      >
        {display ? (
          <span className="text-slate-900">{display}</span>
        ) : (
          <span className="text-slate-400 italic">empty</span>
        )}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <input
        ref={inputRef}
        autoFocus
        // A number column uses a text input on purpose: `type="number"` refuses
        // the thousands separators and currency signs people paste off an
        // invoice, and silently keeps an unparseable draft. We clean and check
        // the value on save instead. A date column does too (D109.6): the native
        // date control follows the browser locale, so typed 20/10/2026 landed as
        // 01/02/2026. The text is parsed day-first on save.
        type="text"
        inputMode={type === "number" ? "decimal" : undefined}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (err) setErr(null);
        }}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={() => type === "date" && setCalendarOpen(true)}
        onClick={() => type === "date" && setCalendarOpen(true)}
        className="flex-1 min-w-[80px] px-2 py-1 rounded border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-primary-500/25"
        placeholder={type === "date" ? "dd/mm/yyyy" : "empty"}
        disabled={saving}
      />

      {type === "date" && calendarOpen && !saving && (
        <CalendarPopover
          anchor={inputRef.current}
          value={parseTypedDate(draft)}
          onClose={() => setCalendarOpen(false)}
          onPick={(iso) => {
            setCalendarOpen(false);
            const shown = formatDate(iso);
            setDraft(shown);
            void save(shown);
          }}
        />
      )}

      {!hideActions && (
        <>
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            className="px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>

          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs disabled:opacity-50"
          >
            Cancel
          </button>
        </>
      )}

      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}
